"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.buildDefaultBrandNamingConvention = buildDefaultBrandNamingConvention;
exports.normalizeBrandNamingConvention = normalizeBrandNamingConvention;
const naming_convention_catalog_1 = require("./naming-convention-catalog");
const naming_convention_types_1 = require("./naming-convention-types");
/**
 * The convention a brand starts from when it first opens the generator, plus the tolerant reader
 * that reconciles a stored payload against the current catalog.
 */
/** Values the starting convention pre-fills, so the first preview is a real name rather than blanks. */
const STARTING_VALUES = {
    campaign: {
        funnelStage: 'Prospecting',
        campaignGoal: 'Sales',
        campaignType: 'Testing',
        bidStrategy: 'Max Volume',
        uniqueIdentifier: 'Always On',
        budgetOptimization: 'ABO',
    },
    adSet: {
        groupingBatch: 'Ad Angle',
        targeting: 'Open',
        age: '18+',
        gender: 'all Genders',
        region: 'Worldwide',
        placement: 'allPlacements',
        optimizationEvent: 'Purchase',
    },
    ad: {
        date: 'DD-MM-YYYY',
        product: '',
        adFormat: 'Static',
        adConcept: 'Founder Ad',
        creator: 'In-House',
        adAngle: 'Price',
        uniqueIdentifier: 'Always On',
        landingPage: 'Home',
    },
};
function buildDefaultLevel(level) {
    return {
        components: naming_convention_catalog_1.NAMING_COMPONENT_CATALOG[level].map((definition) => ({
            key: definition.key,
            label: definition.label,
            value: STARTING_VALUES[level][definition.key] ?? '',
            included: true,
        })),
        delimiter: '_',
        case: 'Aa',
        spaces: 'nospace',
    };
}
/** A fresh convention: every catalog component included, in catalog order, underscore-joined. */
function buildDefaultBrandNamingConvention() {
    return {
        version: naming_convention_types_1.BRAND_NAMING_CONVENTION_VERSION,
        campaign: buildDefaultLevel('campaign'),
        adSet: buildDefaultLevel('adSet'),
        ad: buildDefaultLevel('ad'),
    };
}
function isRecord(value) {
    return typeof value === 'object' && value !== null && !Array.isArray(value);
}
function parseEnumValue(values, value) {
    return typeof value === 'string' && values.includes(value) ? value : undefined;
}
/**
 * Reconciles one stored level against the catalog: keeps the stored order for components the catalog
 * still defines, drops the rest, and appends any catalog component the payload is missing.
 *
 * Labels come from the catalog rather than the stored row so a label reword reaches saved brands.
 */
function normalizeLevel(level, value) {
    const fallback = buildDefaultLevel(level);
    const root = isRecord(value) ? value : {};
    const storedComponents = Array.isArray(root.components) ? root.components : [];
    const definitions = naming_convention_catalog_1.NAMING_COMPONENT_CATALOG[level];
    const normalized = [];
    const seen = new Set();
    for (const stored of storedComponents) {
        if (!isRecord(stored) || typeof stored.key !== 'string' || seen.has(stored.key))
            continue;
        const definition = definitions.find((candidate) => candidate.key === stored.key);
        if (!definition)
            continue;
        seen.add(definition.key);
        normalized.push({
            key: definition.key,
            label: definition.label,
            value: typeof stored.value === 'string' ? stored.value : '',
            included: typeof stored.included === 'boolean' ? stored.included : true,
        });
    }
    // A component added to the catalog after this brand last saved. Appended rather than inserted at
    // its catalog position, so it can never silently change the meaning of an existing name.
    for (const definition of definitions) {
        if (seen.has(definition.key))
            continue;
        normalized.push({ key: definition.key, label: definition.label, value: '', included: false });
    }
    return {
        components: normalized,
        delimiter: parseEnumValue(naming_convention_types_1.NAMING_DELIMITERS, root.delimiter) ?? fallback.delimiter,
        case: parseEnumValue(naming_convention_types_1.NAMING_CASES, root.case) ?? fallback.case,
        spaces: parseEnumValue(naming_convention_types_1.NAMING_SPACES, root.spaces) ?? fallback.spaces,
    };
}
/**
 * Reads an arbitrary stored payload into a usable convention. Tolerant by design: component keys are
 * free strings on the wire precisely so a catalog change never makes an existing brand's saved
 * convention unreadable.
 */
function normalizeBrandNamingConvention(value) {
    const root = isRecord(value) ? value : {};
    return {
        version: naming_convention_types_1.BRAND_NAMING_CONVENTION_VERSION,
        campaign: normalizeLevel('campaign', root.campaign),
        adSet: normalizeLevel('adSet', root.adSet),
        ad: normalizeLevel('ad', root.ad),
    };
}
